import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Star } from "lucide-react";
const getSupabase = () => import("@/lib/supabase").then(m => m.supabase);
import type { DbLivestock, DistributionType } from "@/types/database";
const heroImage = "/hero-banner.webp";
import TrustBadges from "@/components/TrustBadges";
import DesktopNav from "@/components/DesktopNav";
import TrackerRecoveryForm from "@/components/TrackerRecoveryForm";
import OrderCountdown from "@/components/OrderCountdown";
import { useIsPastDeadline } from "@/hooks/useIsPastDeadline";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  HOW_IT_WORKS_STEPS,
  MOCK_TRACKER_STEPS,
  TRACKING_FEATURES,
  DISTRIBUTION_OPTIONS,
  SECTION_COPY,
} from "@/data/landing";

const TEAM_MEMBERS = {
  advisor: {
    name: "Ustaz Mizi Wahid",
    role: "Religious Advisor",
    image: "/team/ustaz-mizi.jpg",
    desc: "Ustaz Mizi has years of experience in guiding the community through Korban with trust, sincerity, and reliable Islamic knowledge.",
  },
  members: [
    { name: "Tengku Syamil", role: "Growth", image: "/team/tengku-syamil.jpg" },
    { name: "Tengku Hafidz", role: "Product", image: "/team/tengku-hafidz.jpg" },
    { name: "Faruq Rasid", role: "Technology", image: "/team/faruq-rasid.jpg" },
  ],
};

const TeamSection = ({
  onOrder,
  disabled,
}: {
  onOrder: () => void;
  disabled: boolean;
}) => (
  <>
    {/* Mobile */}
    <section className="px-6 py-12 lg:hidden">
      <h2 className="text-lg font-bold text-foreground mb-1">The Team</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Our team combines religious and technological expertise to make your Korban experience seamless and meaningful.
      </p>

      {/* Advisor card */}
      <div className="bg-card rounded-2xl border border-border shadow-sm p-5 flex gap-4 items-center mb-4">
        <div className="flex-shrink-0">
          <div className="w-20 h-20 rounded-full border-[3px] border-primary/20 overflow-hidden">
            <img src={TEAM_MEMBERS.advisor.image} alt={TEAM_MEMBERS.advisor.name} className="w-full h-full object-cover" loading="lazy" width="80" height="80" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-bold tracking-widest uppercase text-primary">{TEAM_MEMBERS.advisor.role}</span>
          <h3 className="text-base font-bold text-foreground mt-0.5">{TEAM_MEMBERS.advisor.name}</h3>
          <div className="w-10 h-0.5 bg-primary rounded-full mt-1.5 mb-2" />
          <p className="text-xs text-muted-foreground leading-relaxed">{TEAM_MEMBERS.advisor.desc}</p>
        </div>
      </div>

      {/* Team members */}
      <div className="grid grid-cols-3 gap-3">
        {TEAM_MEMBERS.members.map((member) => (
          <div key={member.name} className="bg-card rounded-2xl border border-border shadow-sm p-4 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full border-2 border-primary/10 overflow-hidden mb-2">
              <img src={member.image} alt={member.name} className="w-full h-full object-cover" loading="lazy" width="56" height="56" />
            </div>
            <h3 className="text-xs font-bold text-foreground">{member.name}</h3>
            <p className="text-[11px] text-primary font-medium">{member.role}</p>
          </div>
        ))}
      </div>

      <button
        onClick={onOrder}
        disabled={disabled}
        className={cn(
          "btn-primary w-full text-lg mt-6",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        {disabled ? "Orders Closed" : "Order Korban Now"}
      </button>
    </section>

    {/* Desktop */}
    <section className="hidden lg:block border-t border-border">
      <div className="max-w-7xl mx-auto px-16 xl:px-24 py-20">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-primary mb-3">
            Who We Are
          </span>
          <h2 className="text-4xl font-extrabold text-foreground">The Team</h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto text-lg">
            Our team combines religious and technological expertise to make your Korban experience seamless and meaningful.
          </p>
        </div>

        {/* Advisor card */}
        <div className="bg-card rounded-2xl border border-border shadow-sm p-8 flex items-center gap-10 max-w-3xl mx-auto mb-10 hover:shadow-md hover:-translate-y-1 transition-all">
          <div className="flex-shrink-0">
            <div className="w-36 h-36 rounded-full border-4 border-primary/20 overflow-hidden">
              <img src={TEAM_MEMBERS.advisor.image} alt={TEAM_MEMBERS.advisor.name} className="w-full h-full object-cover" loading="lazy" width="144" height="144" />
            </div>
          </div>
          <div>
            <span className="text-xs font-bold tracking-widest uppercase text-primary">{TEAM_MEMBERS.advisor.role}</span>
            <h3 className="text-2xl font-bold text-foreground mt-1">{TEAM_MEMBERS.advisor.name}</h3>
            <div className="w-12 h-0.5 bg-primary rounded-full mt-2 mb-3" />
            <p className="text-sm text-muted-foreground leading-relaxed max-w-md">{TEAM_MEMBERS.advisor.desc}</p>
          </div>
        </div>

        {/* Team members */}
        <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto">
          {TEAM_MEMBERS.members.map((member) => (
            <div key={member.name} className="bg-card rounded-2xl border border-border shadow-sm p-6 flex flex-col items-center text-center hover:shadow-md hover:-translate-y-1 transition-all">
              <div className="w-20 h-20 rounded-full border-2 border-primary/10 overflow-hidden mb-3">
                <img src={member.image} alt={member.name} className="w-full h-full object-cover" loading="lazy" width="80" height="80" />
              </div>
              <h3 className="text-base font-bold text-foreground">{member.name}</h3>
              <p className="text-sm text-primary font-medium">{member.role}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <button
            onClick={onOrder}
            disabled={disabled}
            className={cn(
              "btn-primary !text-base !px-16",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            {disabled ? "Orders Closed" : "Order Korban Now →"}
          </button>
        </div>
      </div>
    </section>
  </>
);

const faqs = [
  {
    q: "When will my Korban be performed?",
    a: "All Korban will be performed between Aidil Adha and Ayyamut Tashriq — the 10th to 13th of Dhul Hijjah — in accordance with Islamic guidelines.",
  },
  {
    q: "Can I receive the meat from my Korban?",
    a: "Yes — if you select the \"Receive meat in Singapore\" distribution option, your Korban meat will be delivered to you. Other distribution options direct all meat to communities in need.",
  },
  {
    q: "How is my Palestine Korban actually distributed?",
    a: "Your Korban is handled by our trusted Korban partner and shipped to Aqaba port in Jordan, where it is distributed across three streams: a portion is processed into ready-to-eat canned meals and delivered to Gaza through the Jordan Hashemite Charity Organisation (JHCO); fresh meat is distributed directly to Palestinians in the West Bank and East Jerusalem; and the balance is distributed to Palestinian refugees in Jordan. If for any reason goods cannot enter the West Bank or Gaza, all Korban meat and canned meals are distributed to Palestinian refugees in Jordan — no Korban is wasted.",
  },
  {
    q: "Can I order more than one Korban of the same type?",
    a: "Yes. Simply increase the quantity when selecting your livestock. All portions will be tracked together under a single order.",
  },
  {
    q: "Can I order different types of Korban (e.g. sheep and cow)?",
    a: "Each order supports one distribution type, so different livestock types cannot be combined in a single order. Simply complete your first order, then place a second one. Each order gets its own tracking page.",
  },
  {
    q: "How do I track my Korban?",
    a: "After placing your order, you'll receive a unique tracking code via email. Check your tracking page anytime to see the current status of your Korban — from confirmation to fulfilment, certificate issuance, and distribution.",
  },
  {
    q: "Will I receive a Korban certificate?",
    a: "Yes. Once your Korban is fulfilled, a certificate will be issued and made available on your tracking page.",
  },
  {
    q: "Will I receive photos of my Korban?",
    a: "Photos are available for Korban performed in Indonesia, Uganda, and Yemen. Depending on the package, you'll receive either before & after photos or a photo of the completed Korban, uploaded to your tracking page within 8 weeks after Eid Al-Adha. Photos are not available for Korban performed in Australia or Palestine.",
  },
  {
    q: "Can I order on behalf of someone else?",
    a: "Absolutely. When entering the Sohibul Korban names, simply enter the name of the person you're ordering on behalf of.",
  },
  {
    q: "What animals are available?",
    a: "We offer sheep, goats, cows, and camels. Sheep and goats count as 1 portion each, while cows and camels can be shared among 7 portions.",
  },
  {
    q: "What payment methods are accepted?",
    a: "We accept all major credit/debit cards and PayNow through our secure Stripe payment gateway.",
  },
  {
    q: "Can I get a refund after ordering?",
    a: "All orders are final and non-refundable once payment is completed.",
  },
];

const FAQSection = () => (
  <Accordion type="single" collapsible className="w-full">
    {faqs.map((faq, i) => (
      <AccordionItem key={i} value={`faq-${i}`}>
        <AccordionTrigger className="text-left text-sm font-semibold hover:no-underline">
          {faq.q}
        </AccordionTrigger>
        <AccordionContent className="text-muted-foreground leading-relaxed">
          {faq.a}
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const StarRating = () => (
  <div className="flex items-center gap-2">
    <div className="flex">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
      ))}
    </div>
    <span className="text-sm font-semibold text-foreground">4.9</span>
    <span className="text-sm text-muted-foreground">(56 ratings)</span>
  </div>
);

const Landing = () => {
  const navigate = useNavigate();
  const isClosed = useIsPastDeadline();
  const handleOrder = (path: string) => () => {
    if (isClosed) return;
    navigate(path);
  };
  const disabledCls = "opacity-50 cursor-not-allowed";
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [selectedDistribution, setSelectedDistribution] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Ref callback: force muted property on DOM element (React's muted attr
  // doesn't reliably set the property, causing iOS to block autoplay)
  const videoRef = useCallback((el: HTMLVideoElement | null) => {
    if (!el) return;
    el.muted = true;
    el.play().catch(() => {});
  }, []);

  const handleCanPlay = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    e.currentTarget.play().catch(() => {});
  };

  // Prefetch livestock data for all distribution types so there's no loading between wizard steps
  useEffect(() => {
    const types: DistributionType[] = ["delivered", "local", "palestine"];
    types.forEach((type) => {
      queryClient.prefetchQuery({
        queryKey: ["livestock", type],
        queryFn: async (): Promise<DbLivestock[]> => {
          const supabase = await getSupabase();
          const { data, error } = await supabase
            .from("korban_livestock")
            .select("id, animal, country, country_flag, emoji, price, display_price, distribution_type, photo_type, photo_timeline, certificate, meat_description, delivery_fees, portions_per_animal, portion_options, is_active, status, fulfilled_at, meat_delivered_at, created_at")
            .eq("distribution_type", type)
            .eq("is_active", true)
            .order("price", { ascending: true });
          if (error) throw error;
          return data || [];
        },
        staleTime: 5 * 60 * 1000,
      });
    });
  }, [queryClient]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Desktop Nav */}
      <DesktopNav />

      {/* Mobile: Full-Viewport Hero (image top + content bottom) */}
      <div className="min-h-[100dvh] flex flex-col lg:hidden">
        {/* Hero Video */}
        <div className="relative flex-1 min-h-[30vh] -mb-4">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlay={handleCanPlay}
            poster={heroImage}
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/hero-vid.webm" type="video/webm" />
            <source src="/hero-vid.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-x-0 -bottom-1 h-40 bg-gradient-to-b from-transparent to-background pointer-events-none" />
          <div className="absolute top-6 left-6 flex items-center gap-2 pointer-events-none">
            <img src="/meem-logo-default.webp" alt="Meem" className="h-4 object-contain drop-shadow-lg" width="16" height="16" />
            <span className="text-xl font-bold text-primary drop-shadow-lg mb-1">
              meem
            </span>
          </div>
        </div>

        {/* Hero Content */}
        <div className="flex flex-col justify-end px-6 py-6">
          <div className="animate-fade-up">
            <h1 className="text-3xl font-bold text-foreground text-balance leading-tight">
              Fulfil Your Korban With Ease & Confidence
            </h1>
            <p className="text-muted-foreground mt-3 text-lg leading-relaxed">
              Order your Korban in minutes. Track the progress while our trusted partners handle the fulfilment.
            </p>
          </div>

          <TrustBadges />

          <div className="pt-4">
            {isClosed ? (
              <TrackerRecoveryForm variant="hero" id="tracker-recovery-hero-mobile" className="mb-4" />
            ) : (
              <OrderCountdown variant="hero" className="mb-4" />
            )}
            <button
              onClick={handleOrder("/choose-type")}
              disabled={isClosed}
              className={cn("btn-primary w-full text-lg", isClosed && disabledCls)}
            >
              {isClosed ? "Orders Closed" : "Order Korban"}
            </button>
            <div className="flex justify-center mt-3">
              <StarRating />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: Two-Column Hero */}
      <div className="hidden lg:flex lg:items-center lg:gap-12 lg:px-16 xl:px-24 lg:py-16 lg:max-w-7xl lg:mx-auto w-full">
        <div className="flex-1 space-y-6">
          <h1 className="text-5xl font-extrabold text-foreground leading-tight text-balance">
            Fulfil Your Korban With Ease & Confidence
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
            Order your Korban in minutes. Track the progress while our trusted partners handle the fulfilment.
          </p>
          <div>
            {isClosed ? (
              <TrackerRecoveryForm variant="hero" id="tracker-recovery-hero-desktop" className="mb-4 max-w-md" />
            ) : (
              <OrderCountdown variant="hero" className="mb-4 max-w-md" />
            )}
            <button
              onClick={handleOrder("/choose-type")}
              disabled={isClosed}
              className={cn("btn-primary !text-base", isClosed && disabledCls)}
            >
              {isClosed ? "Orders Closed" : "Order Korban →"}
            </button>
            <div className="mt-3">
              <StarRating />
            </div>
          </div>
        </div>
        <div className="flex-1 max-w-xl">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlay={handleCanPlay}
            poster={heroImage}
            className="w-full h-80 xl:h-96 object-cover rounded-2xl shadow-[0_16px_48px_-8px_rgba(0,0,0,0.15)]"
          >
            <source src="/hero-vid.webm" type="video/webm" />
            <source src="/hero-vid.mp4" type="video/mp4" />
          </video>
        </div>
      </div>

      {/* ── Mobile: Below-the-fold sections ── */}
      <div className="lg:hidden">
        {/* How It Works */}
        <section className="px-6 py-12 bg-secondary/50">
          <h2 className="text-lg font-bold text-foreground mb-1">{SECTION_COPY.howItWorks.title}</h2>
          <p className="text-sm text-muted-foreground mb-5">{SECTION_COPY.howItWorks.desc}</p>
          <div className="flex flex-col gap-3">
            {HOW_IT_WORKS_STEPS.map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex-shrink-0 pt-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </div>
                </div>
                <div className="flex-1 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
                  <button
                    onClick={() => setExpandedStep(expandedStep === i ? null : i)}
                    className="w-full px-4 py-3 flex items-center gap-3"
                  >
                    <item.icon className="w-5 h-5 text-primary flex-shrink-0" />
                    <h3 className="text-sm font-bold text-foreground flex-1 text-left">{item.title}</h3>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${expandedStep === i ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`grid transition-all duration-200 ${expandedStep === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <p className="px-4 pb-3 text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Track Your Korban */}
        <section className="px-6 py-12">
          <h2 className="text-lg font-bold text-foreground mb-1">{SECTION_COPY.tracker.title}</h2>
          <p className="text-sm text-muted-foreground mb-4">{SECTION_COPY.tracker.desc}</p>

          <ul className="space-y-2 mb-5">
            {TRACKING_FEATURES.map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-primary" />
                </div>
                <span className="text-sm text-foreground">{item}</span>
              </li>
            ))}
          </ul>

          {/* Mini tracker preview */}
          <div className="bg-card rounded-2xl border border-border shadow-sm p-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-50 border border-green-200">
                <span className="text-[11px] font-semibold text-green-700">Order Confirmed</span>
              </span>
              <span className="text-[11px] text-muted-foreground font-medium">A1B2C3</span>
            </div>

            <div className="space-y-0">
              {MOCK_TRACKER_STEPS.map((step, i, arr) => (
                <div key={i} className="flex gap-2.5">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${step.done
                        ? "bg-primary text-primary-foreground"
                        : step.upNext
                          ? "bg-primary/20 text-primary border-2 border-primary animate-pulse-soft"
                          : "bg-secondary text-muted-foreground"
                        }`}
                    >
                      <step.icon className="w-3.5 h-3.5" />
                    </div>
                    {i < arr.length - 1 && (
                      <div className={`w-0.5 flex-1 min-h-[24px] mt-0.5 ${step.done ? "bg-primary" : "bg-border"}`} />
                    )}
                  </div>
                  <div className="pb-4">
                    <p className={`text-sm font-semibold ${step.done ? "text-foreground" : "text-muted-foreground"}`}>
                      {step.title}
                      {step.upNext && (
                        <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                          Up Next
                        </span>
                      )}
                    </p>
                    <p className={`text-xs mt-0.5 ${step.done ? "text-primary font-medium" : "text-muted-foreground"}`}>
                      {step.date}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <TrackerRecoveryForm />
          </div>
        </section>

        {/* Distribution Options (interactive) */}
        <section className="px-6 py-12 bg-secondary/50">
          <h2 className="text-lg font-bold text-foreground mb-2">{SECTION_COPY.distribution.title}</h2>
          <p className="text-sm text-muted-foreground mb-5">{SECTION_COPY.distribution.desc}</p>
          <div className="flex flex-col gap-3">
            {DISTRIBUTION_OPTIONS.map((option) => (
              <div
                key={option.id}
                className={`bg-card rounded-2xl p-4 border-2 shadow-sm transition-all cursor-pointer ${selectedDistribution === option.id
                  ? "border-primary ring-4 ring-primary/10"
                  : "border-transparent"
                  }`}
                onClick={() => setSelectedDistribution(
                  selectedDistribution === option.id ? null : option.id
                )}
              >
                <div className="flex items-center gap-3">
                  <option.icon className={`w-6 h-6 flex-shrink-0 ${selectedDistribution === option.id ? "text-primary" : "text-primary/70"}`} />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-foreground">{option.title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{option.highlight}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => {
              if (isClosed) return;
              if (selectedDistribution) {
                navigate(`/select-livestock?type=${selectedDistribution}`);
              } else {
                navigate("/choose-type");
              }
            }}
            disabled={isClosed}
            className={cn("btn-primary w-full text-lg mt-5", isClosed && disabledCls)}
          >
            {isClosed
              ? "Orders Closed"
              : selectedDistribution
                ? "Continue →"
                : "Order Korban"}
          </button>
          {!selectedDistribution && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              Or select an option above to skip ahead
            </p>
          )}
          <div className="flex justify-center mt-3">
            <StarRating />
          </div>
        </section>

        {/* The Team (mobile) */}
        <TeamSection
          onOrder={handleOrder("/choose-type")}
          disabled={isClosed}
        />

        {/* FAQ + CTA */}
        <section className="px-6 py-12">
          <h2 className="text-lg font-bold text-foreground mb-3">{SECTION_COPY.faq.title}</h2>
          <FAQSection />
          <p className="text-sm text-primary font-semibold text-center mt-8">400+ Korban successfully fulfilled last year</p>
          {!isClosed && <OrderCountdown variant="compact" className="mt-6 mb-3" />}
          <button
            onClick={() => {
              if (isClosed) return;
              if (selectedDistribution) {
                navigate(`/select-livestock?type=${selectedDistribution}`);
              } else {
                navigate("/choose-type");
              }
            }}
            disabled={isClosed}
            className={cn("btn-primary w-full text-lg", isClosed && disabledCls)}
          >
            {isClosed
              ? "Orders Closed"
              : selectedDistribution
                ? "Continue →"
                : "Order Korban"}
          </button>
          <div className="flex justify-center mt-3">
            <StarRating />
          </div>
        </section>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 py-6 bg-secondary/50">
          <img src="/meem-logo-default.webp" alt="Meem" className="h-3 object-contain" width="12" height="12" />
          <span className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} Meem
          </span>
        </div>
      </div>

      {/* Desktop: Trust Badges */}
      <div className="hidden lg:block lg:max-w-7xl lg:mx-auto lg:px-16 xl:px-24 w-full lg:pb-16">
        <div className="border-t border-border pt-10">
          <TrustBadges />
        </div>
      </div>

      {/* ── Desktop: How It Works ── */}
      <section id="how-it-works" className="hidden lg:block bg-secondary/50 border-t border-border">
        <div className="max-w-7xl mx-auto px-16 xl:px-24 py-20">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-primary mb-3">
              {SECTION_COPY.howItWorks.label}
            </span>
            <h2 className="text-4xl font-extrabold text-foreground">
              {SECTION_COPY.howItWorks.title}
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto text-lg">
              {SECTION_COPY.howItWorks.desc}
            </p>
          </div>

          <div className="grid grid-cols-4 gap-8">
            {HOW_IT_WORKS_STEPS.map((item, i) => (
              <div key={i} className="relative group">
                <div className="bg-card rounded-2xl p-6 border border-border shadow-sm h-full flex flex-col transition-all group-hover:shadow-md group-hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-xs font-bold text-primary/50 tracking-wider">
                      STEP {item.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>
                {/* connector line */}
                {i < 3 && (
                  <div className="absolute top-1/2 left-full w-8 border-t-2 border-dashed border-primary/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Desktop: Track Your Order ── */}
      <section id="track-order" className="hidden lg:block border-t border-border">
        <div className="max-w-7xl mx-auto px-16 xl:px-24 py-20">
          <div className="flex items-center gap-16">
            {/* Left: mock tracker */}
            <div className="flex-1">
              <div className="bg-card rounded-2xl border border-border shadow-lg p-8 max-w-md mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-50 border border-green-200">
                    <span className="text-xs font-semibold text-green-700">Order Confirmed</span>
                  </span>
                  <span className="text-xs text-muted-foreground font-medium">A1B2C3</span>
                </div>

                <div className="space-y-0">
                  {MOCK_TRACKER_STEPS.map((step, i, arr) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step.done
                            ? "bg-primary text-primary-foreground"
                            : step.upNext
                              ? "bg-primary/20 text-primary border-2 border-primary animate-pulse-soft"
                              : "bg-secondary text-muted-foreground"
                            }`}
                        >
                          <step.icon className="w-4 h-4" />
                        </div>
                        {i < arr.length - 1 && (
                          <div className={`w-0.5 flex-1 min-h-[32px] mt-1 ${step.done ? "bg-primary" : "bg-border"}`} />
                        )}
                      </div>
                      <div className="pb-6">
                        <p className={`text-sm font-semibold ${step.done ? "text-foreground" : "text-muted-foreground"}`}>
                          {step.title}
                          {step.upNext && (
                            <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                              Up Next
                            </span>
                          )}
                        </p>
                        <p className={`text-xs mt-0.5 ${step.done ? "text-primary font-medium" : "text-muted-foreground"}`}>
                          {step.date}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: copy */}
            <div className="flex-1 space-y-6">
              <span className="inline-block text-xs font-bold tracking-widest uppercase text-primary">
                {SECTION_COPY.tracker.label}
              </span>
              <h2 className="text-4xl font-extrabold text-foreground leading-tight">
                {SECTION_COPY.tracker.title}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                {SECTION_COPY.tracker.desc}
              </p>
              <ul className="space-y-3">
                {TRACKING_FEATURES.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-foreground">
                    <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleOrder("/choose-type")}
                disabled={isClosed}
                className={cn("btn-primary !text-base mt-2", isClosed && disabledCls)}
              >
                {isClosed ? "Orders Closed" : "Order Korban →"}
              </button>
            </div>
          </div>

          <div className="max-w-md mx-auto mt-12">
            <TrackerRecoveryForm />
          </div>
        </div>
      </section>

      {/* ── Desktop: Distribution Options (interactive) ── */}
      <section className="hidden lg:block border-t border-border">
        <div className="max-w-7xl mx-auto px-16 xl:px-24 py-20">
          <div className="text-center mb-14">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-primary mb-3">
              {SECTION_COPY.distribution.label}
            </span>
            <h2 className="text-4xl font-extrabold text-foreground">
              {SECTION_COPY.distribution.title}
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto text-lg">
              {SECTION_COPY.distribution.desc}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {DISTRIBUTION_OPTIONS.map((option) => (
              <div
                key={option.id}
                className={`bg-card rounded-2xl p-8 border-2 shadow-sm hover:shadow-md transition-all cursor-pointer ${selectedDistribution === option.id
                  ? "border-primary shadow-md ring-4 ring-primary/10"
                  : "border-transparent hover:-translate-y-1"
                  }`}
                onClick={() => setSelectedDistribution(
                  selectedDistribution === option.id ? null : option.id
                )}
              >
                <option.icon className={`w-8 h-8 ${selectedDistribution === option.id ? "text-primary" : "text-primary/70"}`} />
                <h3 className="text-xl font-bold text-foreground mt-4 mb-2">
                  {option.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {option.desc}
                </p>
                <span className="inline-block text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                  {option.highlight}
                </span>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => {
                if (isClosed) return;
                if (selectedDistribution) {
                  navigate(`/select-livestock?type=${selectedDistribution}`);
                } else {
                  navigate("/choose-type");
                }
              }}
              disabled={isClosed}
              className={cn("btn-primary !text-base !px-16", isClosed && disabledCls)}
            >
              {isClosed
                ? "Orders Closed"
                : selectedDistribution
                  ? "Continue →"
                  : "Order Korban →"}
            </button>
            {!selectedDistribution && (
              <p className="text-sm text-muted-foreground mt-3">
                Or select an option above to skip ahead
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ── Desktop: The Team ── */}
      <div className="hidden lg:block">
        <TeamSection
          onOrder={handleOrder("/choose-type")}
          disabled={isClosed}
        />
      </div>

      {/* ── Desktop: FAQ ── */}
      <section id="faq" className="hidden lg:block border-t border-border bg-secondary/50">
        <div className="max-w-3xl mx-auto px-16 xl:px-24 py-20">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-primary mb-3">
              {SECTION_COPY.faq.label}
            </span>
            <h2 className="text-4xl font-extrabold text-foreground">
              {SECTION_COPY.faq.title}
            </h2>
          </div>
          <FAQSection />
        </div>
      </section>

      {/* ── Desktop: Final CTA + Testimonials ── */}
      <section className="hidden lg:block border-t border-border">
        <div className="max-w-7xl mx-auto px-16 xl:px-24 py-24">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src={heroImage}
              alt="Korban"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-deep/95 via-primary/90 to-purple-glow/85" />
            <div className="relative py-16 px-12">

              {/* CTA */}
              <div className="text-center">
                <h2 className="text-4xl font-extrabold text-white mb-4 text-balance">
                  Ready to Fulfil Your Korban?
                </h2>
                <p className="text-lg text-white/75 max-w-lg mx-auto mb-4">
                  Join hundreds of families who trust Meem for a seamless ordering experience with transparent status tracking and reliable fulfilment.
                </p>
                <p className="text-white/80 text-sm font-semibold mb-6">
                  400+ Korban successfully fulfilled last year
                </p>
                {!isClosed && (
                  <OrderCountdown
                    variant="compact"
                    theme="dark"
                    className="mb-6 max-w-md mx-auto"
                  />
                )}
                <button
                  onClick={handleOrder("/choose-type")}
                  disabled={isClosed}
                  className={cn(
                    "bg-white text-primary font-bold py-4 px-10 rounded-2xl text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all",
                    isClosed && "opacity-50 cursor-not-allowed",
                  )}
                >
                  {isClosed ? "Orders Closed" : "Order Korban Now →"}
                </button>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-white">4.9</span>
                  <span className="text-sm text-white/60">(56 ratings)</span>
                </div>
                <p className="text-white/50 text-sm mt-4">
                  Seamless process · Transparent tracking · Reliable fulfilment
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Desktop: Footer ── */}
      <footer className="hidden lg:block border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-16 xl:px-24 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/meem-logo-default.webp" alt="Meem" className="h-4 object-contain" />
              <span className="text-lg font-extrabold text-primary">meem</span>
            </div>
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Meem. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
